import React, { useState, useRef, useEffect, useCallback } from "react";

const EnhancedKYCCamera = () => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const folderIconRef = useRef(null);
    const containerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [stream, setStream] = useState(null);
    const [status, setStatus] = useState("Click 'Start Camera' to capture photo");
    const [isAnimating, setIsAnimating] = useState(false);
    const [storedImagesCount, setStoredImagesCount] = useState(0);
    const [uploadProgress, setUploadProgress] = useState(null);
    const Id = localStorage.getItem("userId");

    // Main color: #a41847
    // Background color: rgba(255, 255, 255, 0.05)
    const primaryColor = "#a41847";
    const backgroundColor = "rgba(255, 255, 255, 0.05)";

    // Responsive oval dimensions
    const getOvalDimensions = () => {
        // Get container width to calculate proportional dimensions
        const containerWidth = containerRef.current?.clientWidth || 800;
        return {
            radiusX: Math.min(containerWidth * 0.35, 250),
            radiusY: Math.min(containerWidth * 0.42, 300)
        };
    };

    // Clean up on unmount
    useEffect(() => {
        return () => stopCamera();  // eslint-disable-next-line
    }, []);

    // Attach stream to video element when stream changes
    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(console.error);
        }
    }, [stream]);

    // Start camera stream
    const startCamera = async () => {
        try {
            setIsLoading(true);
            setStatus("Requesting camera access...");

            const constraints = {
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: "user",
                },
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);

            // Wait for video metadata to load before playing
            await new Promise((resolve) => {
                videoRef.current.onloadedmetadata = () => resolve();
            });
            videoRef.current.play();

            // Set canvas dimensions to match video
            if (canvasRef.current) {
                canvasRef.current.width = videoRef.current.videoWidth;
                canvasRef.current.height = videoRef.current.videoHeight;
                const ctx = canvasRef.current.getContext("2d");
                drawOvalGuide(ctx, canvasRef.current);
            }

            setStatus("Camera is active. Position your face within the oval.");
        } catch (err) {
            console.error("Error accessing camera:", err);
            setStatus("Error accessing camera: " + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Stop camera stream
    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
        }
        setStatus("Camera stopped.");
        if (canvasRef.current) {
            const ctx = canvasRef.current.getContext("2d");
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
    }, [stream]);

    // Draw the oval guide
    const drawOvalGuide = (ctx, canvas) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const { radiusX, radiusY } = getOvalDimensions();
        ctx.strokeStyle = primaryColor;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        ctx.stroke();
    };

    // Upload image to database
    const uploadImageToDatabase = async (imageBlob, imageType = "captured") => {
        try {
            setUploadProgress(0);

            // Create form data
            const formData = new FormData();
            formData.append("file", imageBlob, "capture.jpg");
            formData.append("image_type", imageType);
            formData.append("user_id", Id);

            // Use fetch with AbortController for cancelation support
            const controller = new AbortController();
            const signal = controller.signal;

            const response = await fetch(`${process.env.REACT_APP_API_KEY}/images/upload_image_to_DB`, {
                method: 'POST',
                body: formData,
                signal: signal,
            });

            // Set progress to 100% when complete
            setUploadProgress(100);

            if (!response.ok) {
                throw new Error(`Upload failed with status ${response.status}`);
            }

            const result = await response.json();
            setUploadProgress(null);
            return result;
        } catch (error) {
            console.error("Error uploading image:", error);
            setUploadProgress(null);
            throw error;
        }
    };

    // Capture image with animation
    const captureImage = async () => {
        if (!videoRef.current || !stream) {
            setStatus("Please start the camera first");
            return;
        }

        if (isAnimating) {
            return; // Prevent multiple captures during animation
        }

        try {
            const captureCanvas = document.createElement("canvas");
            captureCanvas.width = videoRef.current.videoWidth;
            captureCanvas.height = videoRef.current.videoHeight;
            const ctx = captureCanvas.getContext("2d");

            // Capture the current video frame WITHOUT drawing the oval overlay
            ctx.drawImage(videoRef.current, 0, 0, captureCanvas.width, captureCanvas.height);

            // Get the image as a blob for uploading
            const imageBlob = await new Promise(resolve => {
                captureCanvas.toBlob(blob => resolve(blob), "image/jpeg", 0.9);
            });

            // Get data URL for animation
            const imageDataURL = captureCanvas.toDataURL("image/jpeg");

            // Get folder position for animation target
            const folderRect = folderIconRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            // Start the animation
            setIsAnimating(true);
            setStatus("Processing image...");

            // Create floating image div
            const floatingImg = document.createElement("div");
            floatingImg.className = "floating-image";
            floatingImg.style.backgroundImage = `url(${imageDataURL})`;

            // Set initial position to match the camera view
            floatingImg.style.top = `${containerRect.top + window.scrollY}px`;
            floatingImg.style.left = `${containerRect.left}px`;
            floatingImg.style.width = `${containerRect.width}px`;
            floatingImg.style.height = `${containerRect.height}px`;

            // Calculate the target position (center of folder icon)
            const targetX = folderRect.left + folderRect.width / 2;
            const targetY = folderRect.top + window.scrollY + folderRect.height / 2;

            // Set animation target variables
            document.documentElement.style.setProperty('--target-x', `${targetX}px`);
            document.documentElement.style.setProperty('--target-y', `${targetY}px`);
            document.documentElement.style.setProperty('--start-x', `${containerRect.left}px`);
            document.documentElement.style.setProperty('--start-y', `${containerRect.top + window.scrollY}px`);

            // Add to body for absolute positioning
            document.body.appendChild(floatingImg);

            // Upload the image to database while animation is running
            try {
                const uploadResult = await uploadImageToDatabase(imageBlob);
                console.log("Upload successful:", uploadResult);

                // After animation completes
                setTimeout(() => {
                    // Remove the floating image
                    floatingImg.remove();
                    setIsAnimating(false);
                    setStoredImagesCount(prev => prev + 1);
                    setStatus(`Image captured and stored (${storedImagesCount + 1} photos)`);
                }, 3000);

            } catch (uploadError) {
                // If upload fails, still complete the animation but show error
                console.error("Upload failed:", uploadError);
                setTimeout(() => {
                    floatingImg.remove();
                    setIsAnimating(false);
                    setStatus(`Error storing image: ${uploadError.message}`);
                }, 3000);
            }

        } catch (err) {
            console.error("Error capturing image:", err);
            setStatus("Failed to capture image");
            setIsAnimating(false);
        }
    };

    // Improved Folder Icon SVG with updated colors
    const FolderIcon = () => (
        <svg width="60" height="52" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="folderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#63b3ed" />
                    <stop offset="100%" stopColor="#700e30" />
                </linearGradient>
            </defs>
            <path d="M2 6C2 4.89543 2.89543 4 4 4H9L11 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z"
                fill="url(#folderGradient)" stroke="#500a22" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 6H9L11 8H20C21.1046 8 22 8.89543 22 10V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6C2 5.44772 2.44772 5 3 5H4Z"
                fill="#d81e5e" stroke="#500a22" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    return (
        <div style={{
            backgroundColor: "rgba(255, 255, 255, 0.05)"
        }}>
            <div className="kyc-camera-wrapper" style={{
                width: "100%",
                maxWidth: "600px",
                margin: "5rem auto",
                padding: "1rem",
                position: "relative",
                backgroundColor: backgroundColor,
                borderRadius: "12px"
            }}>
                <h2 style={{ textAlign: "center", marginBottom: "1.5rem", color: primaryColor }}>Take Photo</h2>

                <div className="main-container" style={{ position: "relative" }}>
                    {/* Folder Icon - fixed at top right with margins */}
                    <div
                        ref={folderIconRef}
                        className="folder-icon"
                        style={{
                            position: "fixed",
                            top: "20px", // Stays 20px for both desktop and mobile
                            right: "20px",
                            zIndex: 1000,
                            backgroundColor: backgroundColor,
                            borderRadius: "8px",
                            padding: "5px", // Default: 8px on all sides for desktop
                            boxShadow: "0 3px 8px rgba(0,0,0,0.3)"
                        }}
                    >
                        <FolderIcon />
                        {storedImagesCount > 0 && (
                            <div style={{
                                position: "absolute",
                                top: "-5px",
                                right: "-5px",
                                backgroundColor: primaryColor,
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                fontWeight: "bold",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            }}>
                                {storedImagesCount}
                            </div>
                        )}
                    </div>

                    {status && (
                        <div
                            style={{
                                padding: "0.75rem 1rem",
                                marginBottom: "1rem",
                                borderRadius: "4px",
                                backgroundColor: `${primaryColor}20`,
                                color: primaryColor,
                                border: `1px solid ${primaryColor}`,
                                textAlign: "center",
                            }}
                        >
                            {status}
                        </div>
                    )}

                    {/* Upload Progress Bar */}
                    {uploadProgress !== null && (
                        <div style={{ marginBottom: "1rem" }}>
                            <div style={{ fontSize: "0.875rem", marginBottom: "0.25rem", color: primaryColor }}>
                                Uploading image... {uploadProgress}%
                            </div>
                            <div style={{
                                height: "6px",
                                backgroundColor: "#e2e8f0",
                                borderRadius: "3px",
                                overflow: "hidden"
                            }}>
                                <div
                                    style={{
                                        height: "100%",
                                        width: `${uploadProgress}%`,
                                        backgroundColor: primaryColor,
                                        transition: "width 0.3s ease-in-out"
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div
                        ref={containerRef}
                        className="camera-container"
                        style={{ position: "relative", marginBottom: "1rem" }}
                    >
                        <div style={{
                            position: "relative",
                            border: `1px solid ${primaryColor}`,
                            borderRadius: "8px",
                            overflow: "hidden",
                            maxHeight: "400px",
                            aspectRatio: "4/2"
                        }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{ width: "100%", height: "100%", objectFit: "cover", backgroundColor: "#0a0a0a", display: "block" }}
                            />
                            <canvas
                                ref={canvasRef}
                                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                            />

                            {isLoading && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: "rgba(0,0,0,0.5)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white",
                                        flexDirection: "column",
                                    }}
                                >
                                    <div style={{ marginBottom: "10px" }}>Starting camera...</div>
                                    <div className="spinner"></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className="button-container"
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "1rem",
                            flexWrap: "wrap",
                            marginTop: "1rem",
                        }}
                    >
                        <button
                            onClick={startCamera}
                            disabled={isLoading || stream}
                            style={{
                                padding: "0.75rem 1.5rem",
                                fontSize: "1rem",
                                backgroundColor: primaryColor,
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: stream ? "not-allowed" : "pointer",
                                opacity: isLoading || stream ? 0.7 : 1,
                            }}
                        >
                            Start Camera
                        </button>
                        <button
                            onClick={stopCamera}
                            disabled={isLoading || !stream}
                            style={{
                                padding: "0.75rem 1.5rem",
                                fontSize: "1rem",
                                backgroundColor: "#333333",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: !stream ? "not-allowed" : "pointer",
                                opacity: isLoading || !stream ? 0.7 : 1,
                            }}
                        >
                            Stop Camera
                        </button>
                        <button
                            onClick={captureImage}
                            disabled={isLoading || !stream || isAnimating || uploadProgress !== null}
                            style={{
                                padding: "0.75rem 1.5rem",
                                fontSize: "1rem",
                                backgroundColor: primaryColor,
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: !stream || isAnimating || uploadProgress !== null ? "not-allowed" : "pointer",
                                opacity: isLoading || !stream || isAnimating || uploadProgress !== null ? 0.7 : 1,
                            }}
                        >
                            Capture Image
                        </button>
                    </div>
                </div>

                {/* Animation keyframes and styles - improved for better folder targeting */}
                <style>
                    {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    .spinner {
                        width: 40px;
                        height: 40px;
                        border: 4px solid rgba(255,255,255,0.3);
                        border-top: 4px solid ${primaryColor};
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    
                    .floating-image {
                        position: fixed;
                        background-size: cover;
                        background-position: center;
                        border: 3px solid ${primaryColor};
                        box-shadow: 0 4px 8px rgba(0,0,0,0.3);
                        z-index: 9999;
                        animation: moveToFolder 3s ease-out forwards;
                        pointer-events: none;
                    }
                    
                    @keyframes moveToFolder {
                        0% {
                            transform: scale(1) translate(0, 0);
                            opacity: 0.9;
                        }
                        20% {
                            transform: scale(0.9) translate(0, 0);
                            opacity: 0.9;
                        }
                        100% {
                            transform: translate(
                                calc(var(--target-x) - var(--start-x) - 50%),
                                calc(var(--target-y) - var(--start-y) - 50%)
                            ) scale(0.15);
                            opacity: 0;
                        }
                    }
                    
                    /* Responsive styles */
                    @media (max-width: 768px) {
                        .button-container {
                            flex-direction: row;
                            flex-wrap: wrap;
                        }
                        
                        .button-container button {
                            flex: 1 1 auto;
                            min-width: 120px;
                        }
                    }
                    @media (max-width: 768px) {
                        .folder-icon {
                            top: 80px  !important; 
                        }
                    }
                    @media (max-width: 480px) {
                        .kyc-camera-wrapper {
                            padding: 0.5rem;
                        }
                        
                        .button-container {
                            flex-direction: column;
                        }
                        
                        .button-container button {
                            width: 100%;
                        }
                    }
                `}
                </style>
            </div>
        </div >
    );
};

export default EnhancedKYCCamera;